import Video from "./Video";
import Audio from "./Audio";
import Image from "./Image";
import Text from "./Text";
import { useEffect, useMemo, useRef, useState} from "react";
import Error from "../../../../Alerts/Error";
import {useParams} from "react-router-dom";
import filterResponse from "../../../../../filterResponse";
import uploadToSpaces, { hashFile } from "../../../../../util/uploadToSpaces";
import enqueueUpload from "../../../../../util/uploadQueue";
import { assertNotDuplicateInEntry, releaseClaim, uploadOwner as ownerOf } from "../../../../../util/duplicateFiles";
import Archive from "./Archive";
import Spinner from "../../../../Spinner";
import { checkUploadedFile } from "../../../../../util/uploadQa";

const noHold = () => {};

const UPLOAD_QA_RECHECK_DELAY = 1000;

export default function File({formId, setOtherDisabledReason, setUploadQaHold = noHold, setFileLoading:setLoading, file, answers, setAnswers}) {
	const [error, setError] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [uploadQa, setUploadQa] = useState(null);

	const {id} = useParams();

	// Identifies this input in the page-wide register of files being uploaded (DEV-3191).
	const uploadOwner = ownerOf(formId, file.id);

	// A file still on its way up is only claimed for as long as this input exists. Once the entry
	// is submitted the form goes away and the backend takes over the duplicate check, now that it
	// has an entry to see.
	useEffect(() => () => releaseClaim(uploadOwner), [uploadOwner]);

	// Closing the tab mid-upload is recoverable — the upload resumes when the same file is picked
	// again — but the contributor would still have to find and re-select it, which on a multi-GB
	// file is worth one confirmation prompt to avoid.
	useEffect(() => {
		if (!uploading) {
			return;
		}
		const warn = (event) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [uploading]);

	// A count, not a flag. Every file input in the form shares this setter, so with a boolean the
	// first upload to finish would clear it while another was still running — enabling Submit
	// mid-upload and letting the entry be submitted with an empty file key. Harmless when uploads
	// took seconds; not once they take twenty minutes.
	//
	// `signal` is the input's Cancel button. A cancelled upload answers like a refusal, with no key
	// and no message: the input goes back to whatever it held before the file was picked.
	async function uploadFile(fileToUpload, key, onProgress, signal) {
		setLoading(inFlight => inFlight + 1);
		setUploading(true);
		setError(null);

		try {
			// Recording finalize flow: a stream was already appended server-side, just turn it
			// into the final object. This path keeps streaming through the backend.
			if (key) {
				const formData = new FormData();
				formData.append('key', key);
				formData.append('crowdJobId', id);

				const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files', {
					method: "POST",
					credentials: "include",
					body: formData
				});
				const closed = await filterResponse(response);

				if (response.status === 201) {
					return (await response.json()).key;
				}
				// A closed job answers 409 too, and filterResponse has already put its own message
				// up in place of the whole page.
				else if (closed?.jobClosed) {
					return;
				}
				else if (response.status === 409) {
					// The server distinguishes a file this job already holds from one collected in
					// another job (DEV-3046); keep its wording when it sends one.
					const {error} = await response.json().catch(() => ({}));
					setError(error || "This file has already been uploaded!");
					return;
				}
				throw new Error("Wrong status code");
			}

			// Regular file uploads go straight to Spaces, so large files don't get buffered
			// in (and OOM) the backend container. The input id goes along so the backend can
			// enforce this input's size limit rather than trusting the browser to.
			//
			// Every file input on the page shares one queue. An entry can hold seventy of them,
			// and starting each upload the moment its file is picked put seventy hashes and
			// hundreds of parts in flight at once — enough to exhaust the browser and to divide
			// the contributor's uplink so finely that nothing finished. The two stages are handed
			// over separately so the queue can hash the files next in line while the ones ahead
			// of them are on the wire.
			return await enqueueUpload({
				// The entry being filled in has not been submitted, so neither of the server's
				// duplicate checks can see it yet — the same file in two of its inputs is caught
				// here instead, as soon as the queue knows its hash (DEV-3191).
				hash: async () => {
					const hash = await hashFile(fileToUpload, (loaded, total) => onProgress && onProgress(loaded, total, "hashing"), signal);
					assertNotDuplicateInEntry(hash, uploadOwner, {files: answers.files, inputId: file.id});
					return hash;
				},
				upload: (hash) => uploadToSpaces(fileToUpload, id, { onProgress, fileInputId: file.id, hash, signal }),
				onQueued: (position) => onProgress && onProgress(0, fileToUpload.size, "queued", position),
				signal,
			});
		}
		catch (e) {
			// Nothing of this file is on its way up any more, so it must not go on holding its
			// hash against the other inputs.
			releaseClaim(uploadOwner);

			if (e.code === "CANCELLED" || (signal && signal.aborted)) {
				return;
			}
			if (e.code === "DUPLICATE") {
				setError(e.message);
				return;
			}
			if (e.code === "TOO_LARGE") {
				setError(e.message);
				return;
			}
			// The job closed mid-upload; the page has already been replaced by the reason, so
			// there is nothing left to tell this input.
			if (e.code === "JOB_CLOSED") {
				return;
			}
			throw e;
		}
		finally {
			setLoading(inFlight => Math.max(0, inFlight - 1));
			setUploading(false);
		}
	}

	function setFile(fileKey, fileName) {
		setAnswers(prev => {
			const newState = {...prev};
			const newFiles = [
				...newState.files
			];
			newState.files = newFiles;
			const found = newFiles.find(value => value.id === file.id);
			found.fileName = fileName;
			found.key = fileKey;
			return newState;
		});
	}

	const fileKey = useMemo(() => {
		const foundFile = answers.files.find(value => value.id === file.id);
		return foundFile.key;
	}, [answers, file.id]);

	const fileName = useMemo(() => {
		const foundFile = answers.files.find(value => value.id === file.id);
		return foundFile.fileName;
	}, [answers, file.id]);

	// Read at check time rather than made a dependency: the answers change on every keystroke.
	const answersRef = useRef(answers);
	answersRef.current = answers;

	// The metrics read the form as well as the file ("3. layout", "4. URL"), so a verdict goes stale
	// once the contributor edits it. Files are left out: a new file re-runs the check by its key.
	const answersSignature = useMemo(() => JSON.stringify({...answers, files: undefined}), [answers]);

	// The file the last verdict was for, and whether any check actually ran on it. An input with
	// nothing to run on upload is not re-checked on every edit of the form.
	const lastCheck = useRef({key: null, ran: false});

	const crowdMetricTemplateId = file.properties?.crowdMetricTemplateId;

	// Upload-time QA. Keyed on the file, so it also runs for a file already on the entry when the
	// page loads — a draft reopened, or a submitted entry being redone — and a blocker there holds
	// Submit just as it would have straight after the upload. It runs again, once the typing has
	// settled, when the answers change.
	useEffect(() => {
		if (!crowdMetricTemplateId || !fileKey) {
			lastCheck.current = {key: null, ran: false};
			setUploadQa(null);
			setUploadQaHold(file.id, false);
			return;
		}

		const fileChanged = lastCheck.current.key !== fileKey;
		if (!fileChanged && !lastCheck.current.ran) {
			return;
		}

		let active = true;
		const abortController = new AbortController();

		// Held from the first keystroke, so Save cannot go out on the verdict for the old answers.
		setUploadQaHold(file.id, true);

		const timer = setTimeout(() => {
			setUploadQa({checking: true});
			// Counted as checked the moment it goes out: the check can take a while, and typing
			// meanwhile must wait for the debounce, not send a fresh check on every keystroke. An
			// aborted check is still run by the workers, so each one sent costs them a full run.
			lastCheck.current = {key: fileKey, ran: true};

			checkUploadedFile({
				crowdJobId: id,
				fileInputId: file.id,
				key: fileKey,
				fileName,
				answers: answersRef.current,
				signal: abortController.signal,
			})
				.then(result => {
					if (!active) {
						return;
					}
					lastCheck.current = {key: fileKey, ran: !!result.checked};
					setUploadQa(result);
					const blocked = !!(result.checked && result.qaBlocked);
					// A blocked file already holds Submit; only a file that may still go in needs its
					// warnings confirmed on Submit.
					setUploadQaHold(file.id, blocked, result.checked && !blocked ? result.errors.map(message => `${file.name}: ${message}`) : []);
				})
				.catch(e => {
					if (!active) {
						return;
					}
					// Nothing to tell the contributor: submit runs every check again. Let the file through.
					console.error(`Upload QA check failed for ${fileKey}; letting the file through unchecked.`, e);
					lastCheck.current = {key: fileKey, ran: false};
					setUploadQa(null);
					setUploadQaHold(file.id, false);
				});
		}, fileChanged ? 0 : UPLOAD_QA_RECHECK_DELAY);

		return () => {
			active = false;
			clearTimeout(timer);
			abortController.abort();
		};
	}, [fileKey, crowdMetricTemplateId, answersSignature]); // eslint-disable-line react-hooks/exhaustive-deps

	// An input that goes away (a modal closed) must not leave Submit held behind it.
	useEffect(() => () => setUploadQaHold(file.id, false), [file.id, setUploadQaHold]);

	const showUploadQa = uploadQa?.checked && (uploadQa.errors.length > 0 || uploadQa.qaBlocked);

	let fileComponent = null;

	switch (file.type) {
		case "image":
			fileComponent = <Image formId={formId} fileKey={fileKey} fileName={fileName} setFile={setFile} uploadFile={uploadFile} file={file}/>;
			break;
		case "audio":
			fileComponent = <Audio formId={formId} setOtherDisabledReason={setOtherDisabledReason} fileKey={fileKey} setFile={setFile} uploadFile={uploadFile} file={file}/>;
			break;
		case "video":
            fileComponent = <Video formId={formId}  fileKey={fileKey} setFile={setFile} uploadFile={uploadFile} file={file}/>;
			break;
        case "text":
			fileComponent = <Text formId={formId} fileKey={fileKey} fileName={fileName} setFile={setFile} uploadFile={uploadFile} file={file}/>;
			break;
        case "archive":
			fileComponent = <Archive formId={formId} fileKey={fileKey} fileName={fileName} setFile={setFile} uploadFile={uploadFile} file={file}/>;
			break;
		default:
			fileComponent = null;
			break;
	}

	return (
		// Marked as done once the entry holds a file for it. The picker itself always reads "No file
		// chosen" after an upload, so without this the contributor cannot tell a finished input from
		// an empty one without playing its preview.
		<div className={"mb-3" + (fileKey && !uploading ? " input-done" : "")}>
			{fileComponent}
			{fileKey && !uploading &&
				<div className={"mt-2 text-success small"}>
					<i className={"bi bi-check-circle-fill me-1"} />
					Uploaded{fileName ? `: ${fileName}` : ""}. Choose a file again only to replace it.
				</div>
			}
			{error && <Error message={error} />}
			{uploadQa?.checking &&
				<div className="mt-2">
					<Spinner small={true} />
					<small className="text-muted ms-2">Checking file…</small>
				</div>}
			{showUploadQa &&
				<div className={`alert ${uploadQa.qaBlocked ? "alert-danger" : "alert-warning"} mt-2 mb-0`} role="alert">
					<strong>{uploadQa.qaBlocked ? "This file failed a required check. Fix it, or replace the file, to continue." : "This file has QA warnings. You can continue, but please check them."}</strong>
					{uploadQa.errors.length > 0 &&
						<ul className="mb-0 mt-1">
							{uploadQa.errors.map((message, index) => <li key={index}>{message}</li>)}
						</ul>}
				</div>}
		</div>
	)
}